// Px3 Pipeline - the admin tab shell.
//
// Six sub-views on one tab (spec 6). This file owns exactly three things and
// nothing else: which sub-view is showing, which order is open, and the toast.
// Every view fetches and polls its own data, because their cadences differ (a
// detail moves every few seconds, the profile list never does) and a shared
// fetcher here would either over-poll the quiet ones or under-poll the live one.
//
// SECURITY: nothing in this tree can render a borrowed credential. The order
// rows carry only `has_*` booleans and `secrets_purged_at`. The two deliberate
// cleartext exceptions are both house convention and both explicit: a new source
// API key, shown once at creation, and a mailbox password revealed on demand
// from the dedicated decrypt endpoint.

import { useCallback, useEffect, useRef, useState } from 'react';

import './Px3.css';
import { OrdersList } from './px3/OrdersList';
import { OrderDetail } from './px3/OrderDetail';
import { PoolView } from './px3/PoolView';
import { ProfilesView } from './px3/ProfilesView';
import { QueueView } from './px3/QueueView';
import { SourcesView } from './px3/SourcesView';
import { SubmitOrder } from './px3/SubmitOrder';

const TOAST_MS = 8000;

export type Px3ToastKind = 'success' | 'error' | 'info' | 'warn';

/** The signature every sub-view gets for talking back to the operator. Exported
 *  because all seven of them take it and one definition beats seven copies. */
export type Px3ShowToast = (kind: Px3ToastKind, message: string) => void;

type Px3View = 'orders' | 'queue' | 'pool' | 'sources' | 'profiles' | 'submit';

const VIEWS: { id: Px3View; label: string }[] = [
  { id: 'orders', label: 'Orders' },
  { id: 'queue', label: 'Queue' },
  { id: 'pool', label: 'Pool' },
  { id: 'sources', label: 'Sources' },
  { id: 'profiles', label: 'Profiles' },
  { id: 'submit', label: 'Submit' },
];

interface Toast {
  type: Px3ToastKind;
  message: string;
}

export function Px3(): JSX.Element {
  const [view, setView] = useState<Px3View>('orders');
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  /**
   * One toast at a time, one timer at a time.
   *
   * The previous timer has to be CANCELLED, not merely outlived: a toast raised
   * near the end of an earlier toast's window is otherwise wiped by that earlier
   * timer firing. Several messages here exist only as a toast (the count of rows
   * a retry moved, the exclusions that were already present), so a stray timer
   * would swallow the only report of what a click did. Cleared on unmount too,
   * so the callback cannot fire into a dead component.
   */
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback<Px3ShowToast>((type, message) => {
    if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    setToast({ type, message });
    toastTimer.current = window.setTimeout(() => {
      toastTimer.current = null;
      setToast(null);
    }, TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current !== null) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  const openOrder = useCallback((orderId: string) => {
    setView('orders');
    setOpenOrderId(orderId);
  }, []);

  // The detail view takes over the whole tab: it has its own header, its own
  // rail and five tabs of its own, and leaving the sub-nav on screen above all
  // that invites a click that silently throws away what the operator was
  // reading. Back returns to the list.
  if (openOrderId !== null) {
    return (
      <div className="px3">
        <div className="px3-scroll">
          {toast && <div className={`px3-toast px3-toast--${toast.type}`}>{toast.message}</div>}
          <OrderDetail
            orderId={openOrderId}
            onBack={() => setOpenOrderId(null)}
            showToast={showToast}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="px3">
      <div className="px3-scroll">
        <header className="px3-header">
          <h1 className="px3-title">Px3 Pipeline</h1>
          <p className="px3-sub">
            End-to-end package builds driven by a customer portal over the intake API: domains routed
            to Cloudflare, seats claimed and licensed, DNS written, domains added and verified on
            Google, mailboxes renamed, photographed, IMAPs-pushed, then connected to Instantly and
            MasterInbox. One order walks twelve stages; this dashboard is where an operator watches
            it and unsticks it. Every borrowed credential is held only for the life of the order.
          </p>
        </header>

        <nav className="px3-subnav">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`px3-subnav-item ${view === v.id ? 'active' : ''}`}
              onClick={() => setView(v.id)}
            >
              {v.label}
            </button>
          ))}
        </nav>

        {toast && <div className={`px3-toast px3-toast--${toast.type}`}>{toast.message}</div>}

        {view === 'orders' && <OrdersList onOpen={openOrder} showToast={showToast} />}
        {view === 'queue' && <QueueView />}
        {view === 'pool' && <PoolView showToast={showToast} />}
        {view === 'sources' && <SourcesView showToast={showToast} />}
        {view === 'profiles' && <ProfilesView />}
        {view === 'submit' && <SubmitOrder onOpenOrder={openOrder} showToast={showToast} />}
      </div>
    </div>
  );
}
